import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building, Home, Mail, Lock, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const LandlordLogin = () => {
  const navigate = useNavigate();
  const { isArabic } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock authentication - store in localStorage
    localStorage.setItem('landlordLoggedIn', 'true');
    localStorage.setItem('landlordName', formData.name || formData.email.split('@')[0]);
    localStorage.setItem('landlordEmail', formData.email);
    navigate('/landlord/dashboard');
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FF8C42] rounded-full mb-4">
            <Building className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#404040]">
            {isArabic ? 'بوابة الملاك' : 'Landlord Portal'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isArabic 
              ? 'أدر عقاراتك وأضف قوائم جديدة'
              : 'Manage your properties and add new listings'
            }
          </p>
        </div>

        <Card className="border-none shadow-xl">
          <CardHeader>
            <div className="flex space-x-2 mb-4">
              <Button
                variant={isLogin ? "default" : "outline"}
                onClick={() => setIsLogin(true)}
                className={`flex-1 ${isLogin ? 'bg-[#FF8C42] hover:bg-[#FF6B35]' : ''}`}
              >
                {isArabic ? 'تسجيل الدخول' : 'Login'}
              </Button>
              <Button
                variant={!isLogin ? "default" : "outline"}
                onClick={() => setIsLogin(false)}
                className={`flex-1 ${!isLogin ? 'bg-[#FF8C42] hover:bg-[#FF6B35]' : ''}`}
              >
                {isArabic ? 'إنشاء حساب' : 'Register'}
              </Button>
            </div>
            <CardTitle className="text-center text-[#404040]">
              {isLogin 
                ? (isArabic ? 'مرحباً بعودتك' : 'Welcome Back')
                : (isArabic ? 'إنشاء حساب جديد' : 'Create New Account')
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">{isArabic ? 'الاسم الكامل' : 'Full Name'}</Label>
                  <div className="relative">
                    <User className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="name"
                      type="text"
                      placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={isArabic ? 'pr-10' : 'pl-10'}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</Label>
                <div className="relative">
                  <Mail className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={isArabic ? 'pr-10' : 'pl-10'}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{isArabic ? 'كلمة المرور' : 'Password'}</Label>
                <div className="relative">
                  <Lock className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                  <Input
                    id="password"
                    type="password"
                    placeholder={isArabic ? 'أدخل كلمة المرور' : 'Enter your password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className={isArabic ? 'pr-10' : 'pl-10'}
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6">
                {isLogin 
                  ? (isArabic ? 'تسجيل الدخول' : 'Login')
                  : (isArabic ? 'إنشاء حساب' : 'Create Account')
                }
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-gray-600">
                {isArabic ? 'هل أنت عميل معماري؟' : 'Are you an architecture client?'}
              </p>
              <Link to="/client/login" className="text-[#FF8C42] hover:text-[#FF6B35] font-medium text-sm">
                {isArabic ? 'اذهب إلى بوابة العملاء المعماريين' : 'Go to Architecture Client Portal'}
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-600 hover:text-[#FF8C42]">
            ← {isArabic ? 'العودة إلى الصفحة الرئيسية' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};
